import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PaperPlaneRight, Circle, ArrowLeft, MagnifyingGlass, ChatCircle as ChatIcon } from "@phosphor-icons/react";

const Chat = () => {
  const { convId } = useParams();
  const { user, sessionToken } = useAuth();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const loadConvs = async () => {
    const r = await api.get("/conversations");
    setConvs(r.data);
  };
  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    if (!convId) { setActive(null); setMessages([]); return; }
    api.get(`/conversations/${convId}/messages`).then((r) => setMessages(r.data));
    const found = convs.find(c => c.id === convId);
    if (found) setActive(found);
  }, [convId, convs]);

  useEffect(() => {
    const token = sessionToken || (typeof window !== "undefined" ? localStorage.getItem("omni_token") : null);
    if (!token) return;
    const ws = new WebSocket(wsUrl(token));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      const data = JSON.parse(ev.data);
      if (data.type === "message") {
        if (data.message.conversation_id === convId) {
          setMessages((m) => {
            if (m.find(x => x.id === data.message.id)) return m;
            return [...m, data.message];
          });
        }
        loadConvs();
      }
    };
    ws.onerror = () => {};
    return () => { try { ws.close(); } catch {} };
  }, [sessionToken, convId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !convId) return;
    const content = text;
    setText("");
    await api.post("/messages", { conversation_id: convId, content });
  };

  const filteredConvs = convs.filter(c => !search || c.other_user?.name?.toLowerCase().includes(search.toLowerCase()));

  const showThread = !!convId;

  return (
    <div className="h-full">
      <div className="grid h-full md:grid-cols-[340px_1fr]">
        {/* Conversation list */}
        <aside
          className={`border-r border-line bg-paper-white overflow-y-auto ${showThread ? "hidden md:block" : "block"}`}
          data-testid="conv-list"
        >
          <div className="border-b border-line p-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-black text-2xl">CHATS</h2>
              <span className="text-xs text-ink-3">{convs.length}</span>
            </div>
            <div className="mt-3 flex border border-line bg-white">
              <span className="px-3 flex items-center text-ink-3"><MagnifyingGlass size={14} /></span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search chats…"
                data-testid="chat-search"
                className="flex-1 p-2 text-sm bg-white"
              />
            </div>
          </div>
          <div className="divide-y divide-line">
            {filteredConvs.length === 0 && (
              <div className="p-8 text-center text-xs text-ink-3">
                — No conversations.<br/>Start one from <button onClick={() => navigate("/app/people")} className="underline font-semibold">People</button>. —
              </div>
            )}
            {filteredConvs.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/app/messages/${c.id}`)}
                className={`w-full text-left p-3 flex gap-3 brut-hover ${convId === c.id ? "bg-paper-tint" : ""}`}
                data-testid={`conv-${c.id}`}
              >
                <img
                  src={c.other_user?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${c.other_user?.user_id}`}
                  className="w-12 h-12 border border-line object-cover"
                  alt=""
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm truncate">{c.other_user?.name}</span>
                    {c.last_message && (
                      <span className="text-[10px] text-ink-3 shrink-0 ml-2">
                        {new Date(c.last_message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 truncate mt-0.5">{c.last_message?.content || "Tap to start chatting"}</div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Thread */}
        <section className={`flex flex-col h-full ${showThread ? "flex" : "hidden md:flex"}`}>
          {!active && !convId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-ink-3 text-sm gap-3 p-8">
              <ChatIcon size={48} weight="duotone" />
              <div>— Select a conversation to start chatting —</div>
            </div>
          ) : (
            <>
              <header className="border-b border-line p-3 flex items-center gap-3 bg-paper-white">
                <button onClick={() => navigate("/app/messages")} className="md:hidden p-1" data-testid="back-to-list">
                  <ArrowLeft size={20} />
                </button>
                <img
                  src={active?.other_user?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${active?.other_user?.user_id}`}
                  className="w-10 h-10 border border-line object-cover"
                  alt=""
                />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold truncate">{active?.other_user?.name || "—"}</div>
                  <div className="text-[10px] text-ink-3 flex items-center gap-1">
                    <Circle size={6} weight="fill" color="#00C853" /> ONLINE · REAL-TIME
                  </div>
                </div>
              </header>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-paper scanline">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[78%] p-3 text-sm font-mono border ${mine ? "bg-ink text-white border-ink" : "bg-paper-white border-line"}`}
                        data-testid={`msg-${m.id}`}
                      >
                        <div className="whitespace-pre-wrap break-words">{m.content}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-paper-tint" : "text-ink-3"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <div className="text-center text-ink-3 text-xs mt-10">— No messages yet. Say hi. —</div>}
              </div>
              <form onSubmit={send} className="border-t border-line flex bg-paper-white">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message…"
                  data-testid="chat-input"
                  className="flex-1 p-4 text-sm bg-paper-white"
                />
                <button type="submit" className="px-5 bg-ink text-white" data-testid="chat-send">
                  <PaperPlaneRight size={16} weight="bold" />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default Chat;
